import React, { useMemo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { QueryResultModel } from "services/api/cplapi/CPLAPI";
import ResultsTable from "app/modules/aiAssistant/conversation/messages/components/resultsTable/ResultsTable";

type Props = {
  content: string;
  queryResults?: { [key: string]: QueryResultModel } | undefined;
};

// The reply marks where a result table goes with {table:resultID} on a line of its own
const TABLE_MARKER = /^\{table:([0-9a-f]{8})\}$/gm;

const MessageBody = ({ content, queryResults }: Props) => {
  const results = useMemo(() => queryResults ?? {}, [queryResults]);

  const referencedResultIDs = new Set(
    [...content.matchAll(TABLE_MARKER)].map(([, id]) => id).filter((id) => id in results)
  );

  // A new renderer each render would rebuild every paragraph, resetting the tables inside them
  const markdownComponents = useMemo<Components>(
    () => ({
      p: ({ children }) => {
        const resultID = getMarkerResultID(children);
        if (resultID === undefined) return <p>{children}</p>;

        // A marker for a result we can't show is dropped rather than shown as text
        return resultID in results && isQueryResultRenderable(results[resultID]) ? (
          <ResultsTable payload={results[resultID]} testIdKey={resultID} />
        ) : null;
      },
      // A link in a reply would otherwise replace the app in this tab
      a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
    }),
    [results]
  );

  return (
    <>
      {/* Blocked so a reply can't leak data through an image URL */}
      <ReactMarkdown components={markdownComponents} disallowedElements={["img"]} unwrapDisallowed>
        {content}
      </ReactMarkdown>
      {Object.entries(results)
        .filter(([id, payload]) => !referencedResultIDs.has(id) && isQueryResultRenderable(payload))
        .map(([id, payload]) => (
          <ResultsTable key={id} payload={payload} testIdKey={id} />
        ))}
    </>
  );
};

// The result ID when the paragraph is nothing but a table marker
const getMarkerResultID = (children: React.ReactNode) => {
  const parts = React.Children.toArray(children);
  if (parts.length !== 1 || typeof parts[0] !== "string") return undefined;

  const matches = [...parts[0].trim().matchAll(TABLE_MARKER)];
  return matches.length === 1 ? matches[0][1] : undefined;
};

const isQueryResultRenderable = (payload: QueryResultModel) => {
  const rowCount = payload.rows?.length ?? 0;
  const columnCount = payload.columns?.length ?? 0;

  if (rowCount === 0) return false;
  if (rowCount === 1 && columnCount === 1) return false;

  return true;
};

export default React.memo(MessageBody);
